// Page for managing auto-closed sessions and reconciling them with accurate cash and bank values.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CalendarClock,
  Coins,
  Landmark,
  PencilLine,
  Save,
  XCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  useGetAutoClosedSessions,
  useReconcileAutoClosedSession,
} from "../service/counterService";
import { AutoClosedSessionItem } from "../types/counterTypes";

type SessionFormState = {
  closingCash: string;
  closingBank: string;
  varianceReason: string;
  remark: string;
};

const emptyForm = (session: AutoClosedSessionItem): SessionFormState => ({
  closingCash: session.closingCash?.toString() ?? "",
  closingBank: session.closingBank?.toString() ?? "",
  varianceReason: session.varianceReason ?? "",
  remark: session.remark ?? "",
});

function SessionManager() {
  const queryClient = useQueryClient();
  const branchId = Number(localStorage.getItem("branchId") || 0);
  const [businessDate, setBusinessDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [page] = useState(0);
  const [pageSize] = useState(10);
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [formState, setFormState] = useState<Record<number, SessionFormState>>(
    {},
  );

  const { data, isLoading, isError, error } = useGetAutoClosedSessions({
    branchId,
    businessDate,
    page,
    size: pageSize,
  });
  const reconcileMutation = useReconcileAutoClosedSession();

  const sessions = data?.content ?? [];

  const startEditing = (session: AutoClosedSessionItem) => {
    const sessionId = session.id ?? session.sessionId;
    if (!sessionId) return;

    setEditingSessionId(sessionId);
    setFormState((prev) => ({
      ...prev,
      [sessionId]: emptyForm(session),
    }));
  };

  const cancelEditing = () => {
    setEditingSessionId(null);
  };

  const updateField = (
    sessionId: number,
    field: keyof SessionFormState,
    value: string,
  ) => {
    setFormState((prev) => ({
      ...prev,
      [sessionId]: {
        ...(prev[sessionId] || emptyForm({} as AutoClosedSessionItem)),
        [field]: value,
      },
    }));
  };

  const handleSave = (session: AutoClosedSessionItem) => {
    const sessionId = session.id ?? session.sessionId;
    if (!sessionId) return;

    const draft = formState[sessionId];
    if (!draft) return;

    const closingCash = Number(draft.closingCash);
    const closingBank = Number(draft.closingBank);

    if (
      !draft.closingCash ||
      !draft.closingBank ||
      Number.isNaN(closingCash) ||
      Number.isNaN(closingBank)
    ) {
      toast.error("Please enter valid closing cash and bank values.");
      return;
    }

    reconcileMutation.mutate(
      {
        sessionId,
        payload: {
          branchId,
          closingCash,
          closingBank,
          varianceReason: draft.varianceReason,
          remark: draft.remark,
        },
      },
      {
        onSuccess: () => {
          toast.success("Session reconciled successfully.");
          queryClient.invalidateQueries({
            queryKey: ["useGetAutoClosedSessions"],
          });
          setEditingSessionId(null);
        },
        onError: (err: Error) => {
          toast.error(err.message || "Unable to reconcile session.");
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#E8EEF9] bg-linear-to-r from-[#F8FAFF] via-white to-[#F4F8FF] p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#D8E3F2] bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[#1A5EF9]">
              <CalendarClock size={14} />
              Auto-closed sessions
            </div>
            <h1 className="text-xl font-semibold text-[#1A2B4A]">
              Session Manager
            </h1>
            <p className="mt-1 text-sm text-[#52627C]">
              Review counter sessions that closed automatically and reconcile
              them with accurate cash and bank values.
            </p>
          </div>

          <div className="w-full rounded-2xl border border-[#E8EEF9] bg-white p-3 shadow-sm lg:w-60">
            <label className="mb-1 block text-sm font-medium text-[#1A2B4A]">
              Business Date
            </label>
            <input
              type="date"
              value={businessDate}
              onChange={(e) => setBusinessDate(e.target.value)}
              className="w-full rounded-xl border border-[#D8E3F2] px-3 py-2 text-sm text-[#1A2B4A] outline-none focus:border-[#1A5EF9]"
            />
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-[#E8EEF9] bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-medium text-[#52627C]">
              <CalendarClock size={16} className="text-[#1A5EF9]" />
              Total sessions
            </div>
            <p className="mt-2 text-2xl font-semibold text-[#1A2B4A]">
              {sessions.length}
            </p>
          </div>
          <div className="rounded-2xl border border-[#E8EEF9] bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-medium text-[#52627C]">
              <Coins size={16} className="text-[#1A5EF9]" />
              Pending review
            </div>
            <p className="mt-2 text-2xl font-semibold text-[#1A2B4A]">
              {sessions.length}
            </p>
          </div>
          <div className="rounded-2xl border border-[#E8EEF9] bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-medium text-[#52627C]">
              <Landmark size={16} className="text-[#1A5EF9]" />
              Branch
            </div>
            <p className="mt-2 text-2xl font-semibold text-[#1A2B4A]">
              {branchId || "-"}
            </p>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-3xl border border-[#E8EEF9] bg-white p-6 text-sm text-[#52627C] shadow-sm">
          Loading auto-closed sessions...
        </div>
      )}

      {isError && (
        <div className="rounded-3xl border border-red-100 bg-red-50 p-6 text-sm text-red-600 shadow-sm">
          {error instanceof Error ? error.message : "Unable to load sessions."}
        </div>
      )}

      {!isLoading && !isError && sessions.length === 0 && (
        <div className="rounded-3xl border border-[#E8EEF9] bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#F0F4FF] text-[#1A5EF9]">
            <AlertCircle size={22} />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-[#1A2B4A]">
            No auto-closed sessions found
          </h2>
          <p className="mt-2 text-sm text-[#52627C]">
            There are no sessions to reconcile for the selected business date.
          </p>
        </div>
      )}

      {!isLoading && !isError && sessions.length > 0 && (
        <div className="space-y-3">
          {sessions.map((session) => {
            const sessionId = session.id ?? session.sessionId;
            const isEditing = editingSessionId === sessionId;
            const draft = sessionId ? formState[sessionId] : undefined;

            return (
              <div
                key={
                  sessionId ?? `${session.counterName}-${session.businessDate}`
                }
                className="rounded-3xl border border-[#E8EEF9] bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1A5EF9]/10 text-[#1A5EF9]">
                      <Coins size={18} />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold text-[#1A2B4A]">
                          {session.counterName || "Counter Session"}
                        </h2>
                        <span className="rounded-full bg-[#F0F4FF] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1A5EF9]">
                          Auto-closed
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-[#52627C]">
                        Session ID: {sessionId || "-"} • Business Date:{" "}
                        {session.businessDate || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!isEditing ? (
                      <button
                        type="button"
                        onClick={() => startEditing(session)}
                        className="flex items-center justify-center gap-2 rounded-xl border border-[#D8E3F2] px-3 py-2 text-sm font-medium text-[#1A5EF9] hover:bg-[#F0F4FF]"
                      >
                        <PencilLine size={16} />
                        Edit
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={cancelEditing}
                        className="flex items-center justify-center gap-2 rounded-xl border border-[#D8E3F2] px-3 py-2 text-sm font-medium text-[#52627C] hover:bg-[#F5F8FD]"
                      >
                        <XCircle size={16} />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 xl:grid-cols-[1.1fr_0.9fr]">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl bg-[#F8FAFF] p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#8A9BB5]">
                        Closing Cash
                      </p>
                      <p className="mt-1 text-sm font-semibold text-[#1A2B4A]">
                        {session.closingCash ?? "-"}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-[#F8FAFF] p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#8A9BB5]">
                        Closing Bank
                      </p>
                      <p className="mt-1 text-sm font-semibold text-[#1A2B4A]">
                        {session.closingBank ?? "-"}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#EEF2FA] bg-[#FCFDFF] p-3">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#8A9BB5]">
                      Session Notes
                    </p>
                    <p className="mt-1 text-sm text-[#52627C]">
                      {session.varianceReason ||
                        "No variance reason provided yet."}
                    </p>
                    <p className="mt-2 text-sm text-[#52627C]">
                      {session.remark || "No additional remark available."}
                    </p>
                  </div>
                </div>

                {!isEditing ? (
                  <div className="mt-4 rounded-2xl border border-[#EEF2FA] bg-[#FCFDFF] p-3 text-sm text-[#52627C]">
                    Review the existing values and update them when a
                    reconciliation adjustment is needed.
                  </div>
                ) : (
                  <div className="mt-4 rounded-[22px] border border-[#D8E3F2] bg-[#FAFCFF] p-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="text-sm text-[#1A2B4A]">
                        <span className="mb-1 block font-medium">
                          Closing Cash
                        </span>
                        <input
                          type="number"
                          value={draft?.closingCash ?? ""}
                          onChange={(e) =>
                            sessionId &&
                            updateField(
                              sessionId,
                              "closingCash",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-xl border border-[#D8E3F2] px-3 py-2 outline-none focus:border-[#1A5EF9]"
                        />
                      </label>
                      <label className="text-sm text-[#1A2B4A]">
                        <span className="mb-1 block font-medium">
                          Closing Bank
                        </span>
                        <input
                          type="number"
                          value={draft?.closingBank ?? ""}
                          onChange={(e) =>
                            sessionId &&
                            updateField(
                              sessionId,
                              "closingBank",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-xl border border-[#D8E3F2] px-3 py-2 outline-none focus:border-[#1A5EF9]"
                        />
                      </label>
                    </div>

                    <label className="mt-3 block text-sm text-[#1A2B4A]">
                      <span className="mb-1 block font-medium">
                        Variance Reason
                      </span>
                      <input
                        type="text"
                        value={draft?.varianceReason ?? ""}
                        onChange={(e) =>
                          sessionId &&
                          updateField(
                            sessionId,
                            "varianceReason",
                            e.target.value,
                          )
                        }
                        className="w-full rounded-xl border border-[#D8E3F2] px-3 py-2 outline-none focus:border-[#1A5EF9]"
                      />
                    </label>

                    <label className="mt-3 block text-sm text-[#1A2B4A]">
                      <span className="mb-1 block font-medium">Remark</span>
                      <textarea
                        rows={3}
                        value={draft?.remark ?? ""}
                        onChange={(e) =>
                          sessionId &&
                          updateField(sessionId, "remark", e.target.value)
                        }
                        className="w-full rounded-xl border border-[#D8E3F2] px-3 py-2 outline-none focus:border-[#1A5EF9]"
                      />
                    </label>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E8EEF9] bg-white p-3 shadow-sm">
                      <p className="text-sm text-[#52627C]">
                        Update the reconciliation details and save the changes.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleSave(session)}
                        disabled={reconcileMutation.isPending}
                        className="flex items-center gap-2 rounded-xl bg-[#1A5EF9] px-4 py-2 text-sm font-medium text-white hover:bg-[#164DCC] disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        <Save size={16} />
                        {reconcileMutation.isPending ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SessionManager;
